<template>
    <div class="summary-report w-full h-full flex flex-col p-2 pt-4">
        <div class="flex pb-2 items-center">
            <transaction-filter
                enable-date-filter
                @update="onFilterUpdated"
            />
            <UButton
                size="sm"
                variant="outline"
                color="neutral"
                icon="i-ph-download"
                @click="showSummaryReportExportsDialog"
            >
                Eksport...
            </UButton>
        </div>
        <loading-bar :style="{visibility: loading ? 'visible' : 'hidden'}" data-cy="loading-indicator"/>
        <div class="flex justify-center h-full w-full">
            <ag-grid-vue
                class="w-full grow summary-report"
                :style="{maxWidth: dynamicMaxWidth+'px'}"
                :theme="agTheme"
                :row-data="reportEntries"
                :grid-options="gridOptions"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import {AgGridVue} from 'ag-grid-vue3'
import {agTheme} from '@/agUtils/ag-theme.service'
import {computed, inject, onMounted, onUnmounted, Ref, ref} from 'vue'
import {getMonthIntervals} from '@/services/reports/summaryReport.service'
import {formatCurrency} from '@/services/formatter.service'
import {SummaryReportTableRow} from '@/types/reports/SummaryReportTableRow'
import {defaultSearchParams, TransactionSearchParams} from '@/types/transaction/TransactionSearchParams'
import {
    ColDef, GetContextMenuItemsParams, GetRowIdParams,
    GridOptions, RowDataUpdatedEvent,
    RowGroupOpenedEvent
} from 'ag-grid-community'
import TransactionFilter from '@/transaction/TransactionFilter.vue'
import {useEmitter} from '@/services/infrastructure/emitter'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED} from '@/types/events'
import LoadingBar from '@/components/LoadingBar.vue'
import GroupHeaderColumnCell from '@/components/reports/GroupHeaderColumnCell.vue'
import ReportNodeCell from '@/components/reports/ReportNodeCell.vue'
import ReportAmountDrillDownCell from '@/components/reports/ReportAmountDrillDownCell.vue'
import {ReportType, SummaryReport, SummaryReportData} from '@/types/reports/Report'
import {showExportsDialog} from '@/exports/exports.service'

const defaultParams = defaultSearchParams()
const fromDate = ref(defaultParams.fromDate!)
const toDate = ref(defaultParams.toDate!)
const project = ref()
const department = ref()
const data = ref<SummaryReportData>()
const loading = ref(true)
const {onEvents, cleanUp} = useEmitter()

const props = defineProps({
    dataFetch: {type: Function, required: true},
    reportType: {
        type: String as () => ReportType,
        required: true
    },
    showIncomingColumn: {
        type: Boolean,
        default: false
    },
    summaryColumnName: {
        type: String,
        default: 'Sum'
    }
})

const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const onFilterUpdated = (searchParms: TransactionSearchParams) => {
    if (searchParms.fromDate !== fromDate.value || searchParms.toDate !== toDate.value || searchParms.project !== project.value || searchParms.department !== department.value) {
        fromDate.value = searchParms.fromDate || defaultSearchParams().fromDate!
        toDate.value = searchParms.toDate || defaultSearchParams().toDate!
        project.value = searchParms.project
        department.value = searchParms.department
        updateReport()
    }
}

const clearData = () => {
    data.value = undefined
}

const updateReport = async () => {
    loading.value = true
    const summaryReport: SummaryReport = await props.dataFetch(fromDate.value, toDate.value, department.value, project.value)
    data.value = summaryReport.data
    setTimeout(() => {
        loading.value = false
    }, 100)
}

const reportEntries = computed(() => {
    if (!data.value) return []

    return flattenTree(data.value)
})

const getAccountRange = (node: any): {fromAccountNo?: string, toAccountNo?: string} => {
    if (node.accountNo) return {fromAccountNo: node.accountNo, toAccountNo: node.accountNo}
    if (node.ranges?.length) {
        const mins = node.ranges.map((r: any) => r.min).sort()
        const maxes = node.ranges.map((r: any) => r.max).sort()
        return {fromAccountNo: mins[0], toAccountNo: maxes[maxes.length - 1]}
    }
    const children = node.groups || node.accounts || []
    const accounts = children
        .flatMap((child: any) => {
            const range = getAccountRange(child)
            return range.fromAccountNo ? [range.fromAccountNo, range.toAccountNo!] : []
        })
        .filter(Boolean)
        .sort()
    if (!accounts.length) return {}
    return {fromAccountNo: accounts[0], toAccountNo: accounts[accounts.length - 1]}
}

const flattenTree = (node: any, path: string[] = []): any[] => {
    const pathName = node.accountNo ? node.accountNo + node.name : node.name
    const currentPath = [...path, pathName]

    const {fromAccountNo, toAccountNo} = node.accountNo ? {fromAccountNo: undefined, toAccountNo: undefined} : getAccountRange(node)
    const baseEntry = {
        name: node.name,
        accountNo: node.accountNo || '',
        fromAccountNo,
        toAccountNo,
        incoming: node.incomingBalance || 0,
        outgoing: node.outgoingBalance || 0,
        ...monthKeys.value.reduce((acc: Record<string, number>, key, index) => {
            acc[key] = node.monthlyMovements?.[index]?.amount || 0
            return acc
        }, {}),
        path: currentPath
    }

    const childrenGroups = node.groups || node.accounts || []

    const children = childrenGroups.flatMap((child: any) => flattenTree(child, currentPath))

    if (children.length > 0 && node.sumName) {
        const summaryEntry = {
            ...baseEntry,
            name: node.sumName,
            path: [...currentPath, 'Sum']
        }
        return [baseEntry, ...children, summaryEntry]
    }

    return [baseEntry, ...children]
}

onMounted(() => {
    onEvents([ORG_SELECTED], () => {
        clearData()
        updateReport()
    })
    onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, DELETE_TRANSACTION], updateReport)
    updateReport()
})

const safeFromDate = computed(() => {
    return (fromDate.value || defaultSearchParams().fromDate) as string
})

const safeToDate = computed(() => {
    return (toDate.value || defaultSearchParams().toDate) as string
})

type AgCellParam = {
    value?: any,
    data: SummaryReportTableRow,
    node: {
        expanded: boolean,
        key?: string
    }
}

const formatAgCurrencyCell = (param: AgCellParam) => formatCurrency(param.value, 0)

const monthKeys = computed(() => {
    return getMonthIntervals(safeFromDate.value, safeToDate.value)
})

const monthDateRange = (monthKey: string): {fromDate: string, toDate: string} => {
    const [year, month] = monthKey.split('-').map(Number)
    const lastDay = new Date(year, month, 0).getDate()
    return {
        fromDate: `${monthKey}-01`,
        toDate: `${monthKey}-${String(lastDay).padStart(2, '0')}`
    }
}

const monthColumns = computed(() => {
    const fromDateDate = new Date(safeFromDate.value)
    const years = new Date(safeToDate.value).getFullYear() + 1 - fromDateDate.getFullYear()
    return new Array(years).fill(0).map((_, index) => {
        const year = fromDateDate.getFullYear() + index
        return {
            groupId: year + '_proflos_year',
            headerName: year,
            headerClass: 'header-right header-background-color',
            headerGroupComponent: GroupHeaderColumnCell,
            children: [...monthKeys.value.filter(key => key.includes(year.toString())).map(monthKey => {
                return {
                    colId: monthKey + '_proflos_month',
                    headerName: monthKeyTitle(monthKey),
                    headerClass: 'header-right header-background-color',
                    cellStyle: {'text-align': 'right', 'overflow': 'visible'},
                    cellRenderer: ReportAmountDrillDownCell,
                    cellRendererParams: monthDateRange(monthKey),
                    field: monthKey,
                    minWidth: 80,
                    maxWidth: 170,
                    hide: !safeToDate.value.includes(year.toString()),
                    flex: 1,
                    resizable: true,
                    sortable: false
                }
            }), {
                colId: 'sum' + year,
                headerName: props.summaryColumnName,
                cellStyle: {'text-align': 'right', 'overflow': 'visible'},
                width: 100,
                maxWidth: 170,
                field: 'outgoing',
                headerClass: 'header-right header-background-color',
                cellRenderer: ReportAmountDrillDownCell,
                cellRendererParams: {fromDate: safeFromDate.value, toDate: safeToDate.value},
                resizable: true,
                sortable: false,
                pinned: 'right'
            }]
        }
    })
})

const childColumnCount = computed(() => {
    return monthColumns.value.reduce((acc, column) => acc + column.children.length, 0) - 1
})

const dynamicMaxWidth = computed(() => {
    return 200 +                                    // Account column
        (props.showIncomingColumn ? 100 : 0) +      // Incoming column
        (childColumnCount.value * 170) +            // 170px per month col
        200                                         // Summary column
})

const monthNameFormatter = new Intl.DateTimeFormat('no-nb', {month: 'short'})
const monthKeyTitle = (monthKey: string) => {
    const date = new Date(monthKey)
    date.setMonth(monthKey.substring(5) as unknown as number - 1)
    return monthNameFormatter.format(date)
}

const columns = computed<ColDef[]>(() => [
    {
        headerName: 'Inngående',
        field: 'incoming',
        cellStyle: {'text-align': 'right'},
        headerClass: 'header-right header-background-color',
        cellRenderer: (params: AgCellParam) => {
            return params.node.expanded ? '' : formatAgCurrencyCell(params)
        },
        hide: !props.showIncomingColumn,
        width: 100,
        sortable: false
    },
    ...monthColumns.value as unknown as ColDef[]
])

const showSummaryReportExportsDialog = () => {
    showExportsDialog(
        props.reportType,
        {
            fromDate: fromDate.value,
            toDate: toDate.value,
            project: project.value,
            department: department.value
        }
    )
}

const gridOptions: GridOptions = {
    context: {
        panelId,
        panelPathSegment
    },
    columnDefs: columns.value,
    treeData: true,
    getDataPath: (data) => data.path,
    enableCellTextSelection: true,
    autoGroupColumnDef: {
        headerName: 'Konto',
        field: 'name',
        headerClass: 'header-left header-background-color',
        sortable: false,
        minWidth: 300,
        contextMenuItems: [],
        cellRendererParams: {
            suppressCount: true,
            isGroupOpenByDefault: false,
            innerRenderer: ReportNodeCell
        }
    },
    groupDefaultExpanded: -1,
    getRowStyle: (params) => {
        if (params.node.key?.includes('Sum') || params.node.key?.includes('Ordinært resultat') || params.node.key?.includes('Resultat før skatt')) {
            return {fontWeight: 700}
        }
    },
    getRowId: (params: GetRowIdParams) => {
        return params.data.accountNo || params.data.name
    },
    onRowDataUpdated(event: RowDataUpdatedEvent<any>) {
        event.api.setGridOption('columnDefs', columns.value)
    },
    onRowGroupOpened: (event: RowGroupOpenedEvent) => {
        event.api.refreshCells({force: true})
    },
    getContextMenuItems: (params: GetContextMenuItemsParams) => {
        const items = (params.defaultItems || []).filter(item => item !== 'export')
        return [
            ...items,
            {
                name: 'Eksporter...',
                icon: '<span class="ag-icon ag-icon-save" />',
                action: showSummaryReportExportsDialog
            }
        ]
    }
}

onUnmounted(cleanUp)

</script>

<style>
.summary-report {

    [col-id*="proflos_year"] {
        font-weight: bolder;
        font-size: 14px;
        cursor: pointer;
    }

    [col-id*="proflos_month"][role="columnheader"] {
        font-weight: normal;
    }
}
</style>